import { useEffect, useState } from 'react';
import { showAlert, showConfirm } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import { BRAND, type Brand } from '@/brand';
import AdminSidebar from '../components/AdminSidebar';
import AdminProfileMenu from '../components/AdminProfileMenu';
import { Plus, Trash2, Pencil, Search, X, Users, CheckCircle2, Handshake, Building2, Save } from 'lucide-react';

const TYPES = ['Referral Partner', 'Free Zone', 'Law Firm', 'Bank', 'Accountant', 'Marketing', 'Other'];
const CHIPS: { label: string; match: (type: string) => boolean }[] = [
  { label: 'All', match: () => true },
  { label: 'Referral', match: (t) => t === 'Referral Partner' },
  { label: 'Free zone', match: (t) => t === 'Free Zone' },
  { label: 'Law firm', match: (t) => t === 'Law Firm' },
  { label: 'Bank', match: (t) => t === 'Bank' },
  { label: 'Other', match: (t) => !['Referral Partner', 'Free Zone', 'Law Firm', 'Bank'].includes(t) },
];

type Partner = {
  id: string;
  name: string;
  type: string;
  /** Which admin owns this row. Absent on rows added before brand scoping. */
  brand?: Brand;
  contactName?: string;
  email?: string;
  phone?: string;
  commissionPct?: string;
  status?: 'active' | 'paused' | 'pending';
  notes?: string;
  addedAt?: string;
};

const newId = () => `p_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

const IS_CORP = BRAND === 'corporate';

/* `partners/current` is a single shared doc, so without this every partner showed up
   in BOTH the accounting and the consulting admin. Rows added before brand scoping
   carry no brand and fall to corporate — the same convention AdminIntake uses, so
   they stay visible to someone instead of disappearing from both views.

   Scoping is applied at render only: handleSave writes the whole `partners` state
   back, so the other brand's rows must never be filtered out of state itself. */
const isMine = (p: Partner) => (p.brand ? p.brand === BRAND : IS_CORP);

const statusPill = (status?: Partner['status']) => {
  const s = status || 'active';
  if (s === 'paused') return { cls: 'ax-pill ax-pill-grey', label: 'Paused' };
  if (s === 'pending') return { cls: 'ax-pill ax-pill-amber', label: 'Pending' };
  return { cls: 'ax-pill ax-pill-green', label: 'Active' };
};

type Draft = { name: string; type: string; status: NonNullable<Partner['status']>; contactName: string; email: string };
const emptyDraft = (): Draft => ({ name: '', type: TYPES[0], status: 'active', contactName: '', email: '' });

export default function AdminPartners({ embedded = false }: { embedded?: boolean }) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [chip, setChip] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Draft>(emptyDraft());

  // ── LOAD (preserved verbatim) ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'partners', 'current'));
        if (snap.exists() && Array.isArray(snap.data().partners)) setPartners(snap.data().partners);
      } catch (e) { console.error('load partners failed', e); }
      finally { setLoading(false); }
    })();
  }, []);

  // ── REMOVE (preserved verbatim) ──────────────────────────────────────────
  const removePartner = async (id: string, name: string) => {
    if (!(await showConfirm(`Remove ${name || 'this partner'}?`, { destructive: true, confirmText: 'Remove' }))) return;
    setPartners(ps => ps.filter(p => p.id !== id));
  };

  // ── SAVE (preserved verbatim) ────────────────────────────────────────────
  const handleSave = async () => {
    const clean = partners.filter(p => (p.name || '').trim());
    setSaving(true);
    try {
      await saveAdminConfig('partners/current', { partners: clean });
      setPartners(clean);
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'partners/current', { count: clean.length });
    } catch (e: any) {
      showAlert(e?.code === 'permission-denied'
        ? 'Save failed — permission denied. This editor needs a Firebase-authenticated admin session.'
        : 'Save failed — check console.');
    } finally { setSaving(false); }
  };

  // ── ADD / EDIT (same local-state mechanic, surfaced through the modal) ────
  const openAdd = () => { setEditingId(null); setForm(emptyDraft()); setModalOpen(true); };
  const openEdit = (p: Partner) => {
    setEditingId(p.id);
    setForm({ name: p.name || '', type: p.type || TYPES[0], status: p.status || 'active', contactName: p.contactName || '', email: p.email || '' });
    setModalOpen(true);
  };
  const submitModal = () => {
    if (editingId) {
      // spread preserves fields not shown in the modal (phone / commissionPct / notes)
      setPartners(ps => ps.map(p => p.id === editingId ? { ...p, ...form } : p));
    } else {
      setPartners(ps => [{ id: newId(), brand: BRAND, ...form, addedAt: new Date().toISOString() }, ...ps]);
    }
    setModalOpen(false);
  };

  const mine = partners.filter(isMine);
  const active = mine.filter(p => p.status !== 'paused').length;
  const referrals = mine.filter(p => p.type === 'Referral Partner').length;

  const activeChip = CHIPS.find(c => c.label === chip);
  const q = query.trim().toLowerCase();
  const filtered = mine.filter(p => {
    if (activeChip && !activeChip.match(p.type || '')) return false;
    if (!q) return true;
    return [p.name, p.type, p.contactName, p.email].some(v => (v || '').toLowerCase().includes(q));
  });

  const kpis = [
    { label: 'Partners', value: mine.length, icon: <Users className="w-5 h-5" /> },
    { label: 'Active', value: active, icon: <CheckCircle2 className="w-5 h-5" /> },
    { label: 'Referral partners', value: referrals, icon: <Handshake className="w-5 h-5" /> },
  ];

  /* Content is separated from the page chrome so AdminPartnersHub can render it
     inside its own shell as a tab, without a second sidebar and topbar. */
  const content = (
    <>
        <style>{`
          .adminx { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0f172a; }
          .adminx * { box-sizing: border-box; }
          .adminx .ax-topbar { display:flex; align-items:center; justify-content:flex-end; gap:12px; background:#fff; border-bottom:1px solid #e5e7eb; padding:14px 28px; position:sticky; top:0; z-index:20; }
          .adminx .ax-head { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:24px; }
          .adminx .ax-title { font-size:28px; font-weight:700; letter-spacing:-0.02em; margin:0; }
          .adminx .ax-sub { font-size:14px; color:#64748b; margin:6px 0 0; }
          .adminx .ax-head-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
          .adminx .ax-saved { display:inline-flex; align-items:center; gap:6px; color:#16a34a; font-size:12px; font-weight:600; }

          .adminx .ax-btn { display:inline-flex; align-items:center; gap:8px; border:none; border-radius:10px; padding:10px 16px; font-size:14px; font-weight:600; cursor:pointer; transition:background .15s, box-shadow .15s, opacity .15s; font-family:inherit; }
          .adminx .ax-btn:disabled { opacity:.6; cursor:default; }
          .adminx .ax-btn svg { width:16px; height:16px; }
          .adminx .ax-btn-primary { background:#2563EB; color:#fff; }
          .adminx .ax-btn-primary:hover:not(:disabled) { background:#1d4ed8; }
          .adminx .ax-btn-ghost { background:#fff; color:#334155; border:1px solid #e2e8f0; }
          .adminx .ax-btn-ghost:hover:not(:disabled) { background:#f8fafc; }

          .adminx .ax-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin-bottom:20px; }
          .adminx .ax-stat { background:#fff; border:1px solid #eef1f5; border-radius:16px; padding:20px; box-shadow:0 1px 2px rgba(16,24,40,.04); }
          .adminx .ax-stat-ic { width:40px; height:40px; border-radius:12px; display:grid; place-items:center; background:#eff6ff; color:#2563EB; margin-bottom:14px; }
          .adminx .ax-stat-val { font-size:28px; font-weight:700; line-height:1; font-variant-numeric:tabular-nums; }
          .adminx .ax-stat-lbl { font-size:13px; color:#64748b; font-weight:500; margin-top:8px; }

          .adminx .ax-card { background:#fff; border:1px solid #eef1f5; border-radius:16px; box-shadow:0 1px 2px rgba(16,24,40,.04); overflow:hidden; }
          .adminx .ax-toolbar { padding:18px 20px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
          .adminx .ax-search { position:relative; flex:1; min-width:220px; }
          .adminx .ax-search svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#94a3b8; }
          .adminx .ax-search input { width:100%; border:1px solid #e2e8f0; border-radius:10px; padding:10px 12px 10px 36px; font-size:14px; font-family:inherit; color:#0f172a; outline:none; transition:border .15s, box-shadow .15s; }
          .adminx .ax-search input:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
          .adminx .ax-chips { display:flex; gap:8px; flex-wrap:wrap; }
          .adminx .ax-chip { border:1px solid #e2e8f0; background:#fff; color:#475569; border-radius:999px; padding:7px 14px; font-size:13px; font-weight:500; cursor:pointer; font-family:inherit; transition:all .15s; }
          .adminx .ax-chip:hover { background:#f8fafc; }
          .adminx .ax-chip.is-active { background:#eff6ff; border-color:#bfdbfe; color:#1d4ed8; font-weight:600; }

          .adminx .ax-table { width:100%; border-collapse:collapse; }
          .adminx .ax-table th { text-align:left; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; padding:12px 20px; border-bottom:1px solid #f1f5f9; }
          .adminx .ax-table td { padding:16px 20px; border-bottom:1px solid #f4f6f9; font-size:14px; vertical-align:middle; }
          .adminx .ax-table tr:last-child td { border-bottom:none; }
          .adminx .ax-table tr:hover td { background:#fafbfc; }
          .adminx .ax-pname { font-weight:600; color:#0f172a; }
          .adminx .ax-type { color:#475569; }
          .adminx .ax-contact { color:#0f172a; font-weight:500; }
          .adminx .ax-contact small { display:block; color:#94a3b8; font-weight:400; margin-top:2px; }
          .adminx .ax-muted { color:#cbd5e1; }

          .adminx .ax-pill { display:inline-flex; align-items:center; padding:3px 12px; border-radius:999px; font-size:12px; font-weight:600; }
          .adminx .ax-pill-green { background:#dcfce7; color:#15803d; }
          .adminx .ax-pill-amber { background:#fef3c7; color:#b45309; }
          .adminx .ax-pill-grey { background:#f1f5f9; color:#64748b; }

          .adminx .ax-actions { display:flex; gap:6px; justify-content:flex-end; }
          .adminx .ax-icon-btn { display:inline-flex; align-items:center; gap:6px; border:1px solid #e2e8f0; background:#fff; color:#475569; border-radius:8px; padding:6px 10px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; transition:all .15s; }
          .adminx .ax-icon-btn svg { width:14px; height:14px; }
          .adminx .ax-icon-btn:hover { background:#f8fafc; }
          .adminx .ax-icon-btn.danger:hover { background:#fef2f2; color:#dc2626; border-color:#fecaca; }

          .adminx .ax-empty { padding:64px 20px; text-align:center; }
          .adminx .ax-empty svg { width:48px; height:48px; margin:0 auto 12px; color:#e2e8f0; }
          .adminx .ax-empty p { font-size:14px; font-weight:600; color:#64748b; margin:0 0 4px; }
          .adminx .ax-empty span { font-size:13px; color:#94a3b8; }

          .adminx .ax-spin { display:flex; justify-content:center; padding:72px 0; }
          .adminx .ax-spin i { width:32px; height:32px; border:3px solid #2563EB; border-top-color:transparent; border-radius:50%; animation:ax-rot .7s linear infinite; display:block; }
          @keyframes ax-rot { to { transform:rotate(360deg); } }

          .adminx .ax-overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:20px; z-index:50; }
          .adminx .ax-modal { background:#fff; border-radius:18px; width:100%; max-width:480px; box-shadow:0 20px 50px rgba(16,24,40,.25); overflow:hidden; }
          .adminx .ax-modal-head { display:flex; align-items:center; justify-content:space-between; padding:20px 22px; border-bottom:1px solid #f1f5f9; }
          .adminx .ax-modal-head h3 { margin:0; font-size:17px; font-weight:700; }
          .adminx .ax-modal-x { background:none; border:none; cursor:pointer; color:#94a3b8; padding:4px; border-radius:8px; display:inline-flex; }
          .adminx .ax-modal-x:hover { background:#f1f5f9; color:#475569; }
          .adminx .ax-modal-body { padding:22px; display:grid; gap:16px; }
          .adminx .ax-modal-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
          .adminx .ax-field label { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#94a3b8; margin-bottom:6px; }
          .adminx .ax-field input, .adminx .ax-field select { width:100%; border:1px solid #e2e8f0; border-radius:10px; padding:10px 12px; font-size:14px; font-family:inherit; color:#0f172a; outline:none; background:#fff; transition:border .15s, box-shadow .15s; }
          .adminx .ax-field input:focus, .adminx .ax-field select:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
          .adminx .ax-modal-foot { padding:16px 22px; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:10px; }

          @media (max-width:720px) {
            .adminx .ax-stats { grid-template-columns:1fr; }
            .adminx .ax-modal-row { grid-template-columns:1fr; }
            .adminx .ax-table thead { display:none; }
          }
        `}</style>

        {!embedded && (
          <div className="ax-topbar">
            <AdminProfileMenu />
          </div>
        )}

        <div className={embedded ? '' : 'ax-wrap'}>
          {/* Page header */}
          <div className="ax-head">
            <div>
              <h1 className="ax-title">Partners</h1>
              <p className="ax-sub">Referral partners, free zones, law firms, banks and others you work with.</p>
            </div>
            <div className="ax-head-actions">
              {savedAt && <span className="ax-saved"><CheckCircle2 className="w-4 h-4" /> Saved {savedAt}</span>}
              <button className="ax-btn ax-btn-ghost" onClick={handleSave} disabled={saving}>
                <Save /> {saving ? 'Saving…' : 'Save changes'}
              </button>
              <button className="ax-btn ax-btn-primary" onClick={openAdd}>
                <Plus /> Add partner
              </button>
            </div>
          </div>

          {/* Stat cards */}
          <div className="ax-stats">
            {kpis.map(k => (
              <div key={k.label} className="ax-stat">
                <div className="ax-stat-ic">{k.icon}</div>
                <div className="ax-stat-val">{k.value}</div>
                <div className="ax-stat-lbl">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Directory card */}
          <div className="ax-card">
            <div className="ax-toolbar">
              <div className="ax-search">
                <Search />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search partners…" />
              </div>
              <div className="ax-chips">
                {CHIPS.map(c => (
                  <button key={c.label} className={`ax-chip ${chip === c.label ? 'is-active' : ''}`} onClick={() => setChip(c.label)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="ax-spin"><i /></div>
            ) : partners.length === 0 ? (
              <div className="ax-empty">
                <Building2 />
                <p>No partners yet</p>
                <span>Add your first partner to start building your network.</span>
                <div style={{ marginTop: 16 }}>
                  <button className="ax-btn ax-btn-primary" onClick={openAdd}><Plus /> Add your first partner</button>
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="ax-empty">
                <Search />
                <p>No partners match</p>
                <span>Try a different search or filter.</span>
              </div>
            ) : (
              <table className="ax-table">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Type</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => {
                    const pill = statusPill(p.status);
                    return (
                      <tr key={p.id}>
                        <td><span className="ax-pname">{p.name || 'Untitled partner'}</span></td>
                        <td><span className="ax-type">{p.type || '—'}</span></td>
                        <td>
                          {p.contactName || p.email ? (
                            <span className="ax-contact">
                              {p.contactName || '—'}
                              {p.email && <small>{p.email}</small>}
                            </span>
                          ) : <span className="ax-muted">—</span>}
                        </td>
                        <td><span className={pill.cls}>{pill.label}</span></td>
                        <td>
                          <div className="ax-actions">
                            <button className="ax-icon-btn" onClick={() => openEdit(p)}><Pencil /> Edit</button>
                            <button className="ax-icon-btn danger" onClick={() => removePartner(p.id, p.name)}><Trash2 /> Remove</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Add / Edit modal */}
        {modalOpen && (
          <div className="ax-overlay" onClick={() => setModalOpen(false)}>
            <div className="ax-modal" onClick={e => e.stopPropagation()}>
              <div className="ax-modal-head">
                <h3>{editingId ? 'Edit partner' : 'Add partner'}</h3>
                <button className="ax-modal-x" onClick={() => setModalOpen(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="ax-modal-body">
                <div className="ax-field">
                  <label>Partner name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Free Zone" />
                </div>
                <div className="ax-modal-row">
                  <div className="ax-field">
                    <label>Type</label>
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                      {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="ax-field">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Draft['status'] }))}>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="paused">Paused</option>
                    </select>
                  </div>
                </div>
                <div className="ax-field">
                  <label>Contact name</label>
                  <input value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} placeholder="Jane Doe" />
                </div>
                <div className="ax-field">
                  <label>Contact email</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@partner.com" />
                </div>
              </div>
              <div className="ax-modal-foot">
                <button className="ax-btn ax-btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
                <button className="ax-btn ax-btn-primary" onClick={submitModal}>
                  {editingId ? 'Save partner' : 'Add partner'}
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) return <div className="adminx">{content}</div>;

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />
      <div className="flex-1 xl:ml-64 adminx">{content}</div>
    </div>
  );
}
